import { Box, Input, type SxProps, type Theme, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type { ComponentViewProps } from '@/admins/SmartReports/components/Components/types';
import type { EditSmartReportContextType } from '@/admins/SmartReports/components/EditSmartReport/types';
import { COMPONENT_TYPES_ICONS } from '@/admins/SmartReports/constants';
import { useGetPageComponent } from '@/admins/SmartReports/hooks/useGetPageComponent';
import { SmartReportComponentTypesEnum } from '@/admins/SmartReports/types';
import { useEditSmartReport } from '../../EditSmartReport/EditSmartReportContext';

const styles = {
  root: {
    height: '100%',
    width: '100%',
    padding: 1,
    cursor: 'pointer',
    border: (theme: Theme) => `4px dashed ${theme.palette.baseColors.gray.c100}`,
    borderRadius: 2,
    '&:hover': {
      backgroundColor: (theme: Theme) => theme.palette.baseColors.gray.c75,
      cursor: 'pointer',
    },
  },
  input: {
    width: '100%',
    fontSize: '2rem',
    fontWeight: 'bold',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    backgroundColor: 'transparent',
  },
  text: {
    width: '100%',
    fontSize: '2rem',
    fontWeight: 'bold',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    backgroundColor: 'transparent',
  },
  icon: {
    fontSize: 40,
    opacity: 0.25,
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c100,
  },
  placeholder: {
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c75,
  },
};

export const TEXT_FIELD_PLACEHOLDER_ID = 'text_field_placeholder';
const TEXT_FIELD_PLACEHOLDER_TEXT = 'Drag and Drop Text Field or Type Here';

export type TextFieldPlaceholderProps = {
  slotId: string;
  sx?: SxProps<Theme>;
};

export const TextFieldPlaceholder = ({ slotId, sx }: TextFieldPlaceholderProps) => {
  const { onComponentLoad, selectedPage } =
    useEditSmartReport() as unknown as EditSmartReportContextType;
  const { slotData } = useGetPageComponent(selectedPage, slotId);

  const [text, setText] = useState(() => slotData || '');
  const [isEditing, setIsEditing] = useState(false);
  const isPlaceholder = useMemo(() => text === '', [text]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Update text if slotData changes externally
  useEffect(() => {
    if (slotData !== text) {
      setText(slotData || '');
    }
  }, [slotData]);

  const Icon = COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.TextField];

  const handleTextClick = () => setIsEditing(true);
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setText(e.target.value || '');
  const handleTextBlur = () => {
    setIsEditing(false);

    if (onComponentLoad) {
      onComponentLoad({ componentId: TEXT_FIELD_PLACEHOLDER_ID, slotId, data: text });
    }
  };

  return (
    <Box sx={{ ...styles.root, ...sx }}>
      {isEditing ? (
        <Input
          className='textFieldPlaceholderInput'
          sx={styles.input}
          type='text'
          autoFocus
          value={text}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
        />
      ) : (
        <Box
          display='flex'
          flexDirection='row'
          alignItems='center'
          sx={{ ...styles.text, ...(isPlaceholder ? styles.placeholder : {}) }}
          onClick={handleTextClick}
        >
          {isPlaceholder && (
            <Box mr={1}>
              <Icon className='textFieldPlaceholderIcon' sx={styles.icon} />
            </Box>
          )}
          <Typography
            className='textFieldPlaceholderText'
            fontFamily='Inter'
            fontWeight='bold'
            fontSize='2rem'
            flex={1}
          >
            {text || TEXT_FIELD_PLACEHOLDER_TEXT}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export const TextFieldPlaceholderView = ({ data, sx }: ComponentViewProps<string>) => {
  return <Box sx={{ ...sx }}>{data}</Box>;
};
